<template>
  <v-container fluid>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
  
    <v-row class="align-center mb-4 flex-nowrap" no-gutters>
      <!-- Title + Spot -->
      <div class="d-flex align-center mr-4">
        <h2 class="text-h5 mb-0 mr-2">NIFTY</h2>
        <v-chip class="ma-0 font-weight-bold" color="secondary" text-color="black">
          {{ spotPrice }}
        </v-chip>
      </div>
  
      <!-- Expiry chips -->
      <div class="d-flex align-center">
        <v-divider vertical class="mx-4" style="background-color: #000000; width: 4px; opacity: 1;"></v-divider>
        <v-chip v-for="expiry in sortedExpiryDates.slice(0, 3)" :key="expiry"
          :color="selectedExpiry === expiry ? 'primary' : 'grey'" @click="changeExpiry(expiry)" class="ma-1">
          {{ formatDisplayDate(expiry) }}
        </v-chip>
  
        <v-menu>
          <template #activator="{ props }">
            <v-chip v-bind="props" class="ma-1" color="secondary">
              More
            </v-chip>
          </template>
          <v-list>
            <v-list-item v-for="expiry in sortedExpiryDates.slice(3)" :key="expiry" @click="changeExpiry(expiry)">
              {{ formatDisplayDate(expiry) }}
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn color="secondary" icon variant="text" size="large" title="Refresh PnL" class="ml-1" @click="loadData">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
      </div>
    </v-row>
  
  
    <v-table v-if="!loading" fixed-header height="600px">
      <colgroup>
        <col style="width:6%">
        <col style="width:6%">
        <col style="width:6%">
        <col style="width:6%">
        <col style="width:6%">
        <col style="width:10%"> <!-- STRIKE -->
        <col style="width:6%">
        <col style="width:6%">
        <col style="width:6%">
        <col style="width:6%">
        <col style="width:6%">
      </colgroup>
  
      <thead class="tbl-head">
        <tr>
          <th>OI</th>
          <th>Chg OI</th>
          <th>IV</th>
          <th>LTP</th>
          <th>Chg%</th>
          <th class="text-center">STRIKE</th>
          <th>LTP</th>
          <th>Chg%</th>
          <th>IV</th>
          <th>Chg OI</th>
          <th>OI</th>
        </tr>
      </thead>
  
      <tbody>
        <tr v-for="row in displayedOptions" :key="row.strikePrice"
          :class="{ 'nearest-spot': isNearestStrike(row.strikePrice) }">
          <!-- CE cells -->
          <td :class="{ 'light-grey': row.strikePrice < spotPrice }">{{ row.CE?.openInterest ?? '-' }}</td>
          <td :class="{ 'light-grey': row.strikePrice < spotPrice }">{{ row.CE?.changeinOpenInterest ?? '-' }}</td>
          <td :class="{ 'light-grey': row.strikePrice < spotPrice }">{{ row.CE?.impliedVolatility ?? '-' }}</td>
          <td :class="{ 'light-grey': row.strikePrice < spotPrice }">{{ row.CE?.lastPrice ?? '-' }}</td>
          <td :class="[getChangeClass(row.CE?.pChange), { 'light-grey': row.strikePrice < spotPrice }]">
            {{ formatPercent(row.CE?.pChange) }}
          </td>
  
          <!-- Strike -->
          <td class="text-center font-weight-bold">{{ row.strikePrice }}</td>
  
          <!-- PE cells -->
          <td :class="{ 'light-grey': row.strikePrice > spotPrice }">{{ row.PE?.lastPrice ?? '-' }}</td>
          <td :class="[getChangeClass(row.PE?.pChange), { 'light-grey': row.strikePrice > spotPrice }]">
            {{ formatPercent(row.PE?.pChange) }}
          </td>
          <td :class="{ 'light-grey': row.strikePrice > spotPrice }">{{ row.PE?.impliedVolatility ?? '-' }}</td>
          <td :class="{ 'light-grey': row.strikePrice > spotPrice }">{{ row.PE?.changeinOpenInterest ?? '-' }}</td>
          <td :class="{ 'light-grey': row.strikePrice > spotPrice }">{{ row.PE?.openInterest ?? '-' }}</td>
        </tr>
      </tbody>
    </v-table>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

// Debugging state
const debug = ref({
  apiResponse: null,
  dataStructure: null,
  computedValues: null
})

// Reactive State
const rawData = ref([])
const loading = ref(true)
const selectedExpiry = ref(null)

const spotPrice = computed(() => {
  const firstOption = rawData.value.find(
    o => o?.PE?.underlyingValue || o?.CE?.underlyingValue
  )
  return firstOption?.PE?.underlyingValue || firstOption?.CE?.underlyingValue || 0
})

const expiryDates = computed(() => {
  if (!rawData.value.length) return []
  const dates = [...new Set(rawData.value.map(o => o?.expiryDate).filter(Boolean))]
  return dates.sort((a, b) => new Date(a) - new Date(b))
})

const sortedExpiryDates = computed(() => expiryDates.value)

const displayedOptions = computed(() => {
  if (!selectedExpiry.value || !rawData.value.length) return []

  // Filter by selected expiry
  const filtered = rawData.value.filter(o => o.expiryDate === selectedExpiry.value)

  // Since each object already contains both CE and PE,
  // just sort by strikePrice and return
  return filtered.sort((a, b) => a.strikePrice - b.strikePrice)
})
// Methods remain the same...

// Enhanced load function with debugging
const loadData = async () => {
  try {
    loading.value = true
    const response = await store.dispatch('strategy/fetchOptionChain')

    rawData.value = response?.optionChainList || []

    // Set initial expiry from raw expiryDates after data is loaded
    if (expiryDates.value.length > 0) {
      selectedExpiry.value = expiryDates.value[0]  // raw string from data
      console.log('Initial expiry set to:', selectedExpiry.value)
    }
  } catch (error) {
    console.error('Failed to load option chain:', error)
  } finally {
    loading.value = false
  }
}

const changeExpiry = (expiry) => {
  console.log('expiry:', expiry)
  selectedExpiry.value = expiry
}
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}
const formatPercent = (val) => (val != null ? val.toFixed(2) + '%' : '-')

const getChangeClass = (val) => {
  if (val == null) return ''
  return val > 0 ? 'text-success' : val < 0 ? 'text-error' : ''
}
onMounted(loadData)

// Debugging: Watch for changes
watch(selectedExpiry, (newVal) => {
  console.log('Expiry changed to:', newVal)
  console.log('Current displayedOptions:', displayedOptions.value)
})
const nearestDiff = computed(() => {
  if (!displayedOptions.value.length) return Infinity;
  return Math.min(
    ...displayedOptions.value.map(o => Math.abs(o.strikePrice - spotPrice.value))
  );
});
const isNearestStrike = (strike) => {
  const strikes = displayedOptions.value.map(r => r.strikePrice);
  const sorted = [...strikes].sort((a, b) => Math.abs(a - spotPrice.value) - Math.abs(b - spotPrice.value));

  // Pick two nearest
  const nearestTwo = sorted.slice(0, 2);
  return nearestTwo.includes(strike);
}
  </script>

  <style scoped>
/* Header */
  .tbl-head th {
    background-color: #fbeee6 !important;
    color: black !important;
    font-size: 12px;
    font-weight: bold !important;
    padding: 2px 4px;
    border: 1px solid #ddd;
    text-align: center !important;
    vertical-align: middle !important;
    line-height: 1.2 !important;
  }
  
  /* Table cells */
  .v-table th,
  .v-table td {
    font-size: 12px;
    padding: 1px 3px !important;
    /* reduced padding */
    white-space: nowrap;
    border: 1px solid #ddd;
    text-align: center;
    vertical-align: middle;
    line-height: 1.2;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
    height: 32px !important;
    /* fixed row height */
    /* keeps rows shorter */
  }
  
  /* Alternate row colors */
  .v-table tbody tr:nth-child(even) {
    background-color: #f9f6ef;
  }
  
  .v-table tbody tr:nth-child(odd) {
    background-color: #ffffff;
  }
  
  /* LTP & Strike clickable look */
  .v-table td a,
  .strike-link {
    color: #1a4fa1;
    font-weight: bold;
    text-decoration: none;
  }
  
  .v-table td a:hover,
  .strike-link:hover {
    text-decoration: underline;
  }
  
  /* Positive change green, negative red */
  .text-success {
    color: #008000 !important;
    font-weight: bold;
  }
  
  .text-error {
    color: #cc0000 !important;
    font-weight: bold;
  }
  
  /* Footer */
  .tbl-foot td {
    background-color: #3f2b96 !important;
    color: white !important;
    font-weight: bold;
    text-align: center;
  }
  
  /* Column widths */
  col {
    width: 6% !important;
  }
  
  col:nth-child(6) {
    width: 8% !important;
  }
  
  .light-grey {
    background-color: #f0f0f0 !important;
  }
  
  .nearest-spot {
    background-color: #e0e0e0 !important;
  }
  
  /* Remove Vuetify's alternating row background */
  .v-table tbody tr:nth-child(even),
  .v-table tbody tr:nth-child(odd) {
    background-color: transparent !important;
  }
  
  /* Set your own uniform background */
  .v-table tbody tr {
    background-color: #fdf5f0 !important;
    /* your preferred color */
  }
  
  .nearest-spot td {
    background-color: #f8f5d8 !important;
    /* warm highlight */
  }
</style>

